import { useEffect, useRef, useState } from 'react';

// Enhanced parallax hook with fade effect and dramatic movement
// strength: multiplier for movement (e.g., 0.1-1.5), max: clamp in px, bounce: enables fade instead of bounce
export default function useParallax({ strength = 0.2, max = 30, bounce = false, disableOpacity = false } = {}) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let rafId = 0;

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight;
        // Enhanced progress calculation for much more dramatic effect
        const progress = 1 - Math.min(Math.max((rect.top + rect.height / 2) / viewportH, 0), 1);
        
        // Apply enhanced easing function for much more noticeable movement
        let easedProgress;
        if (bounce) {
          // Enhanced sine wave with more dramatic movement
          easedProgress = Math.sin(progress * Math.PI * 1.5) * 1.3;
        } else {
          // Smooth fade effect with dramatic movement
          easedProgress = progress * progress * progress * (progress * (progress * 6 - 15) + 10);
        }
        
        // Amplified movement calculation for maximum visibility
        const movement = Math.max(Math.min((easedProgress - 0.5) * 3 * max * strength, max), -max);
        setOffset(movement);
        
        // Calculate fade effect based on element position
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = viewportH / 2;
        const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
        const maxDistance = viewportH / 2;
        
        // Fade calculation: 1.0 when centered, 0.6 when at edges
        const fadeOpacity = Math.max(0.6, 1 - (distanceFromCenter / maxDistance) * 0.4);
        setOpacity(fadeOpacity);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [strength, max, bounce, disableOpacity]);

  const style = { 
    transform: `translateY(${offset.toFixed(2)}px)`,
    opacity: (bounce || disableOpacity) ? 1 : opacity, // Apply fade effect when bounce is false and opacity is not disabled
    transition: (bounce || disableOpacity) ? 'transform 0.05s ease-out' : 'transform 0.05s ease-out, opacity 0.1s linear'
  };
  return [ref, style];
}
